import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
// import * as Sentry from "@sentry/react"; // Sentry disabled for performance
import { AuthProvider } from './context/AuthContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import { DashboardProvider } from './context/DashboardContext';
import { StatisticsProvider } from './context/StatisticsContext';
import { WalletProvider } from './context/WalletContext';
import { ProtectedRoute, PublicOnlyRoute } from './components/ProtectedRoute';
import { AdminProtectedRoute, AdminPublicOnlyRoute } from './components/AdminProtectedRoute';
import DashboardLayout from './components/DashboardLayout';

import AdminLayout from './components/AdminLayout';
import PersistentChatLayout from './components/PersistentChatLayout';
import StaticFileProxy from './components/StaticFileProxy';
import AccessibilityEnhancer from './components/AccessibilityEnhancer';
import PerformanceMonitor from './components/PerformanceMonitor';
import SecurityMonitor from './components/SecurityEnhancer';
import LoadingScreen from './components/LoadingScreen';
import './styles/rtl.css';

// Lazy load ALL pages to improve initial bundle size
// الصفحات العامة
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PreviewLandingPage = lazy(() => import('./pages/PreviewLandingPage'));
const StoreLandingPage = lazy(() => import('./pages/StoreLandingPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const TrackOrderPage = lazy(() => import('./pages/TrackOrderPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const PlansPage = lazy(() => import('./pages/PlansPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));

// صفحات الإدارة
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const AdminTransactionsPage = lazy(() => import('./pages/AdminTransactionsPage'));
const AdminTasksPage = lazy(() => import('./pages/AdminTasksPage'));
const AdminNotificationsPage = lazy(() => import('./pages/AdminNotificationsPage'));
const AdminFeedbackPage = lazy(() => import('./pages/AdminFeedbackPage'));
const AdminAuditLogsPage = lazy(() => import('./pages/AdminAuditLogsPage'));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'));
const AdminOrdersPage = lazy(() => import('./pages/AdminOrdersPage'));
const AdminAbandonedOrdersPage = lazy(() => import('./pages/AdminAbandonedOrdersPage'));
const AdminTicketsPage = lazy(() => import('./pages/AdminTicketsPage'));
const AdminSupportMessagesPage = lazy(() => import('./pages/AdminSupportMessagesPage'));
const AdminLandingPagesPage = lazy(() => import('./pages/AdminLandingPagesPage'));
const AdminProductsPage = lazy(() => import('./pages/AdminProductsPage'));
const AdminAdminsPage = lazy(() => import('./pages/AdminAdminsPage'));
const AdminAIPage = lazy(() => import('./pages/AdminAIPage'));
const AdminSubscriptionPlansPage = lazy(() => import('./pages/AdminSubscriptionPlansPage'));
const AdminSystemConfigPage = lazy(() => import('./pages/AdminSystemConfigPage'));
const AdminSystemHealthPage = lazy(() => import('./pages/AdminSystemHealthPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/AdminAnalyticsPage'));
const AdminLiveUrlsPage = lazy(() => import('./pages/AdminLiveUrlsPage'));
const AdminWebhooksPage = lazy(() => import('./pages/AdminWebhooksPage'));
const AdminPaymentMethodsPage = lazy(() => import('./pages/AdminPaymentMethodsPage'));
const AdminSecurityConfigPage = lazy(() => import('./pages/AdminSecurityConfigPage'));
const AdminMonitoringPage = lazy(() => import('./pages/AdminMonitoringPage'));
const AdminPerformancePage = lazy(() => import('./pages/AdminPerformancePage'));
const AdminConfigurationPage = lazy(() => import('./pages/AdminConfigurationPage'));
const AdminEmailConfigPage = lazy(() => import('./pages/AdminEmailConfigPage'));
const AdminGCSConfigPage = lazy(() => import('./pages/AdminGCSConfigPage'));
const AdminCustomDomainsPage = lazy(() => import('./pages/AdminCustomDomainsPage'));
const AdminLandingPageEditsPage = lazy(() => import('./pages/AdminLandingPageEditsPage'));

// God-Mode Admin Panel Pages
const AdminCouponsPage = lazy(() => import('./pages/AdminCouponsPage'));
const AdminVisitorsPage = lazy(() => import('./pages/AdminVisitorsPage'));
const AdminInjectionsPage = lazy(() => import('./pages/AdminInjectionsPage'));
const AdminHtmlReviewsPage = lazy(() => import('./pages/AdminHtmlReviewsPage'));
const AdminCacheManagementPage = lazy(() => import('./pages/AdminCacheManagementPage'));

// Critical Admin Tools Pages
const AdminCriticalToolsPage = lazy(() => import('./pages/AdminCriticalToolsPage'));
const AdminTaagerManagementPage = lazy(() => import('./pages/AdminTaagerManagementPage'));
const AdminSubscriptionToolsPage = lazy(() => import('./pages/AdminSubscriptionToolsPage'));
const AdminSubscriptionManagementPage = lazy(() => import('./pages/AdminSubscriptionManagementPage'));
const AdminUserAuditPage = lazy(() => import('./pages/AdminUserAuditPage'));
const AdminOnboardingPage = lazy(() => import('./pages/AdminOnboardingPage'));
const AdminWhatsAppPage = lazy(() => import('./pages/AdminWhatsAppPage'));

// صفحات لوحة التحكم
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const StatisticsPage = lazy(() => import('./pages/StatisticsPage'));
const AbandonedOrdersPage = lazy(() => import('./pages/AbandonedOrdersPage'));
const LandingPagesPage = lazy(() => import('./pages/LandingPagesPage'));
const CreateLandingPage = lazy(() => import('./pages/CreateLandingPage'));
const HTMLEditorPage = lazy(() => import('./pages/HTMLEditorPage'));
const JSInjectionPage = lazy(() => import('./pages/JSInjectionPage'));
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'));
const DepositPage = lazy(() => import('./pages/DepositPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const TicketDetailsPage = lazy(() => import('./pages/TicketDetailsPage'));
const TicketsPage = lazy(() => import('./pages/TicketsPage'));
const TaagerPage = lazy(() => import('./pages/TaagerPage'));
const SafkaPage = lazy(() => import('./pages/SafkaPage'));
const FacebookPage = lazy(() => import('./pages/FacebookPage'));
const WebhookPage = lazy(() => import('./pages/WebhookPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const OnboardingStartPage = lazy(() => import('./pages/OnboardingStartPage'));
const OnboardingGuidePage = lazy(() => import('./pages/OnboardingGuidePage'));

const App = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <div>
      <HelmetProvider>
        <SecurityMonitor>
          <AccessibilityEnhancer>
            <PerformanceMonitor trackRenders={false} trackMemory={false}>
              <AuthProvider>
                <AdminAuthProvider>
                  <PersistentChatLayout>
                    <Suspense fallback={<LoadingScreen />}>
                      <Routes>
                        {/* المسارات العامة - للزوار غير المسجلين */}
                        <Route element={<PublicOnlyRoute />}>
                          <Route path="/" element={<HomePage />} />
                          <Route path="/login" element={<LoginPage />} />
                          <Route path="/register" element={<RegisterPage />} />
                          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                          <Route path="/reset-password" element={<ResetPasswordPage />} />
                        </Route>

                        {/* Public routes for landing pages */}
                        <Route path="/preview/landing-page/:id" element={<PreviewLandingPage />} />
                        <Route path="/store/:storeName/:landingPageName" element={<StoreLandingPage />} />
                        <Route path="/thank-you" element={<ThankYouPage />} />
                        <Route path="/track-order" element={<TrackOrderPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/refund-policy" element={<RefundPolicyPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/features" element={<FeaturesPage />} />
                        <Route path="/pricing" element={<PricingPage />} />

                        {/* Static file proxy route */}
                        <Route path="/static/*" element={<StaticFileProxy />} />

                        {/* المسارات المحمية - للمستخدمين المسجلين */}
                        <Route element={<ProtectedRoute />}>
                          {/* مسارات الإعداد الأولي */}
                          <Route path="/start" element={<OnboardingStartPage />} />
                          <Route path="/onboard" element={<OnboardingGuidePage />} />

                          <Route element={
                            <DashboardProvider>
                              <WalletProvider>
                                <DashboardLayout title="لوحة التحكم" />
                              </WalletProvider>
                            </DashboardProvider>
                          }>
                            <Route path="/dashboard" element={<DashboardPage />} />
                            <Route path="/landing-pages" element={<LandingPagesPage />} />
                            <Route path="/landing-pages/create" element={<CreateLandingPage />} />
                            <Route path="/html-editor/:landingPageId" element={<HTMLEditorPage />} />
                            <Route path="/js-injection/:landingPageId" element={<JSInjectionPage />} />
                            <Route path="/products" element={<ProductsPage />} />
                            <Route path="/orders" element={<OrdersPage />} />
                            <Route path="/abandoned-orders" element={<AbandonedOrdersPage />} />
                            <Route path="/statistics" element={<StatisticsProvider><StatisticsPage /></StatisticsProvider>} />
                            <Route path="/analytics" element={<AnalyticsPage />} />
                            <Route path="/messages" element={<MessagesPage />} />
                            <Route path="/profile" element={<ProfilePage />} />
                            <Route path="/verify" element={<VerifyEmailPage />} />
                            <Route path="/plans" element={<PlansPage />} />
                            <Route path="/wallet/deposit" element={<DepositPage />} />
                            <Route path="/wallet/transactions" element={<TransactionsPage />} />
                            <Route path="/support" element={<SupportPage />} />
                            <Route path="/support/tickets" element={<TicketsPage />} />
                            <Route path="/support/ticket/:ticketId" element={<TicketDetailsPage />} />
                            <Route path="/taager" element={<TaagerPage />} />
                            <Route path="/safka" element={<SafkaPage />} />
                            <Route path="/facebook" element={<FacebookPage />} />
                            <Route path="/webhooks" element={<WebhookPage />} />
                          </Route>

                          {/* صفحة الترحيب للمستخدمين الجدد */}
                          <Route path="/welcome" element={<WelcomePage />} />
                        </Route>

                        {isAdminRoute && (
                          <>
                            <Route element={<AdminPublicOnlyRoute />}>
                              <Route path="/admin/login" element={<AdminLoginPage />} />
                            </Route>

                            <Route element={<AdminProtectedRoute />}>
                              <Route element={<Suspense fallback={<LoadingScreen />}><AdminLayout title="Admin Dashboard" /></Suspense>}>
                                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                                <Route path="/admin/transactions" element={<AdminTransactionsPage />} />
                                <Route path="/admin/tasks" element={<AdminTasksPage />} />
                                <Route path="/admin/notifications" element={<AdminNotificationsPage />} />
                                <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
                                <Route path="/admin/audit-logs" element={<AdminAuditLogsPage />} />
                                <Route path="/admin/users" element={<AdminUsersPage />} />
                                <Route path="/admin/orders" element={<AdminOrdersPage />} />
                                <Route path="/admin/abandoned-orders" element={<AdminAbandonedOrdersPage />} />
                                <Route path="/admin/tickets" element={<AdminTicketsPage />} />
                                <Route path="/admin/support-messages" element={<AdminSupportMessagesPage />} />
                                <Route path="/admin/landing-pages" element={<AdminLandingPagesPage />} />
                                <Route path="/admin/landing-page-edits" element={<AdminLandingPageEditsPage />} />
                                <Route path="/admin/products" element={<AdminProductsPage />} />
                                <Route path="/admin/admins" element={<AdminAdminsPage />} />
                                <Route path="/admin/subscription-plans" element={<AdminSubscriptionPlansPage />} />
                                <Route path="/admin/system-config" element={<AdminSystemConfigPage />} />
                                <Route path="/admin/system-health" element={<AdminSystemHealthPage />} />
                                <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
                                <Route path="/admin/live-urls" element={<AdminLiveUrlsPage />} />
                                <Route path="/admin/webhooks" element={<AdminWebhooksPage />} />
                                <Route path="/admin/payment-methods" element={<AdminPaymentMethodsPage />} />
                                <Route path="/admin/security-config" element={<AdminSecurityConfigPage />} />
                                <Route path="/admin/monitoring" element={<AdminMonitoringPage />} />
                                <Route path="/admin/performance" element={<AdminPerformancePage />} />
                                <Route path="/admin/ai" element={<AdminAIPage />} />
                                <Route path="/admin/configuration" element={<AdminConfigurationPage />} />
                                <Route path="/admin/email-config" element={<AdminEmailConfigPage />} />
                                <Route path="/admin/gcs-config" element={<AdminGCSConfigPage />} />
                                <Route path="/admin/custom-domains" element={<AdminCustomDomainsPage />} />

                                <Route path="/admin/coupons" element={<AdminCouponsPage />} />
                                <Route path="/admin/visitors" element={<AdminVisitorsPage />} />
                                <Route path="/admin/injections" element={<AdminInjectionsPage />} />
                                <Route path="/admin/html-reviews" element={<AdminHtmlReviewsPage />} />
                                <Route path="/admin/cache-management" element={<AdminCacheManagementPage />} />

                                <Route path="/admin/user-management" element={<AdminCriticalToolsPage />} />
                                <Route path="/admin/subscription-tools" element={<AdminSubscriptionToolsPage />} />
                                <Route path="/admin/subscription-management" element={<AdminSubscriptionManagementPage />} />
                                <Route path="/admin/user-audit" element={<AdminUserAuditPage />} />
                                <Route path="/admin/taager-management" element={<AdminTaagerManagementPage />} />
                                <Route path="/admin/onboarding" element={<AdminOnboardingPage />} />
                                <Route path="/admin/whatsapp" element={<AdminWhatsAppPage />} />
                              </Route>
                            </Route>
                          </>
                        )}

                        {/* إعادة توجيه المسارات غير الموجودة */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </Suspense>
                  </PersistentChatLayout>
                </AdminAuthProvider>
              </AuthProvider>
            </PerformanceMonitor>
          </AccessibilityEnhancer>
        </SecurityMonitor>
      </HelmetProvider>
    </div>
  );
};

export default App;
